import React from "react";
import type { LucideIcon } from "lucide-react";

// -----------------------------------------------------------------------------
// Card — subtle dark panel with border
// -----------------------------------------------------------------------------
export function Card({ children, className = "", onClick }: { children: React.ReactNode; className?: string; onClick?: () => void }) {
  const cls = onClick ? "card-hover" : "card";
  return <div onClick={onClick} className={`${cls} ${className}`}>{children}</div>;
}

// -----------------------------------------------------------------------------
// Pill — compact status tag
// -----------------------------------------------------------------------------
export type Tone =
  | "live" | "mobilising" | "acceptance_pending" | "on_hold" | "closed" | "cancelled"
  | "open" | "won" | "lost" | "bid" | "quoted"
  | "approved" | "pending" | "rejected"
  | "critical" | "warning" | "notice" | "expired" | "ok" | "overdue"
  | "default";

const TONE: Record<Tone, string> = {
  live:       "bg-ok/12 text-ok",
  mobilising: "bg-info/12 text-info",
  acceptance_pending: "bg-warn/12 text-warn",
  on_hold:    "bg-text-muted/15 text-text-muted",
  closed:     "bg-text-muted/12 text-text-muted",
  cancelled:  "bg-text-muted/12 text-text-muted",
  open:       "bg-accent/12 text-accent",
  won:        "bg-ok/12 text-ok",
  lost:       "bg-danger/12 text-danger",
  bid:        "bg-info/12 text-info",
  quoted:     "bg-accent/12 text-accent",
  approved:   "bg-ok/12 text-ok",
  pending:    "bg-warn/12 text-warn",
  rejected:   "bg-danger/12 text-danger",
  critical:   "bg-danger/12 text-danger",
  warning:    "bg-warn/12 text-warn",
  notice:     "bg-info/12 text-info",
  expired:    "bg-danger/20 text-danger",
  ok:         "bg-ok/12 text-ok",
  overdue:    "bg-danger/12 text-danger",
  default:    "bg-text/8 text-text",
};

export function Pill({ label, tone = "default", small = false }: { label: string; tone?: Tone; small?: boolean }) {
  const size = small ? "text-[10px] px-1.5 py-0.5" : "text-[11px] px-2 py-[3px]";
  return (
    <span className={`inline-flex items-center gap-1 font-mono font-medium uppercase tracking-wider2 rounded whitespace-nowrap ${size} ${TONE[tone]}`}>
      {label}
    </span>
  );
}

// -----------------------------------------------------------------------------
// Button
// -----------------------------------------------------------------------------
interface BtnProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: "primary" | "secondary" | "ghost" | "danger";
  size?: "sm" | "md" | "lg";
  icon?: LucideIcon;
}

export function Button({ variant = "primary", size = "md", icon: Icon, className = "", children, ...rest }: BtnProps) {
  // 22 Oct 2026 mobile audit: sm was ~28px tall and md ~36px — under a thumb.
  // On a phone every button is at least 40 / 44 px; desktop is unchanged.
  const sizes = { sm: "px-3 py-1.5 text-xs max-md:min-h-[40px] max-md:py-2", md: "px-4 py-2 text-[13px] max-md:min-h-[44px]", lg: "px-5 py-3 text-sm max-md:min-h-[48px]" };
  const iconSize = { sm: 12, md: 14, lg: 16 }[size];
  const variants = {
    primary:   "bg-accent text-black hover:bg-accent-hover",
    secondary: "bg-transparent border border-line-light text-text hover:bg-ink-hover",
    ghost:     "bg-transparent text-text hover:bg-ink-hover",
    danger:    "bg-danger/12 border border-danger/30 text-danger hover:bg-danger/20",
  };
  return (
    <button
      {...rest}
      className={`inline-flex items-center gap-2 rounded font-medium tracking-tight transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${sizes[size]} ${variants[variant]} ${className}`}
    >
      {Icon && <Icon size={iconSize} />}
      {children}
    </button>
  );
}

// -----------------------------------------------------------------------------
// KPI tile
// -----------------------------------------------------------------------------
export function KPI({ label, value, sub, icon: Icon, accent }: {
  label: string; value: React.ReactNode; sub?: string; icon?: LucideIcon; accent?: boolean;
}) {
  return (
    <Card>
      <div className="p-4">
        <div className="flex items-start justify-between">
          <div className="text-text-muted text-[11px] tracking-wider2 uppercase">{label}</div>
          {Icon && <Icon size={14} className={accent ? "text-accent" : "text-text-dim"} />}
        </div>
        <div className={`mt-2 text-[28px] font-semibold tracking-tight ${accent ? "text-accent" : "text-text"}`}>{value}</div>
        {sub && <div className="text-text-muted text-xs mt-0.5">{sub}</div>}
      </div>
    </Card>
  );
}

// -----------------------------------------------------------------------------
// Form field + Input + Select + Textarea
// -----------------------------------------------------------------------------
export function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="text-text-muted text-[11px] uppercase tracking-wider2 font-medium">{label}</label>
      <div className="mt-1.5">{children}</div>
      {hint && <div className="text-text-muted text-[11px] mt-1.5">{hint}</div>}
    </div>
  );
}

export const Input = React.forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement> & { mono?: boolean }>(
  ({ mono, className = "", ...rest }, ref) => (
    <input
      ref={ref}
      {...rest}
      className={`w-full px-3 py-2.5 bg-ink-raised border border-line rounded-md text-text text-sm outline-none focus:border-accent ${mono ? "font-mono" : "font-sans"} ${className}`}
    />
  ),
);
Input.displayName = "Input";

export const Select = React.forwardRef<HTMLSelectElement, React.SelectHTMLAttributes<HTMLSelectElement>>(
  ({ className = "", children, ...rest }, ref) => (
    <select
      ref={ref}
      {...rest}
      className={`w-full px-3 py-2.5 bg-ink-raised border border-line rounded-md text-text text-sm outline-none focus:border-accent cursor-pointer ${className}`}
    >
      {children}
    </select>
  ),
);
Select.displayName = "Select";

export const Textarea = React.forwardRef<HTMLTextAreaElement, React.TextareaHTMLAttributes<HTMLTextAreaElement>>(
  ({ className = "", ...rest }, ref) => (
    <textarea
      ref={ref}
      {...rest}
      className={`w-full px-3 py-2.5 bg-ink-raised border border-line rounded-md text-text text-sm outline-none focus:border-accent resize-y ${className}`}
    />
  ),
);
Textarea.displayName = "Textarea";

// -----------------------------------------------------------------------------
// Section header + Empty state
// -----------------------------------------------------------------------------
export function SectionHeader({ title, action }: { title: string; action?: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between mb-3">
      <h2 className="text-[13px] font-semibold text-text uppercase tracking-wider2">{title}</h2>
      {action}
    </div>
  );
}

export function EmptyState({ title, description, icon: Icon }: { title: string; description?: string; icon?: LucideIcon }) {
  return (
    <div className="p-12 text-center">
      {Icon && (
        <div className="inline-flex w-14 h-14 rounded-full bg-accent/12 items-center justify-center mb-4">
          <Icon size={24} className="text-accent" />
        </div>
      )}
      <div className="text-text font-medium mb-1">{title}</div>
      {description && <div className="text-text-muted text-sm max-w-md mx-auto">{description}</div>}
    </div>
  );
}

// -----------------------------------------------------------------------------
// Spinner
// -----------------------------------------------------------------------------
export function Spinner({ size = 16 }: { size?: number }) {
  return (
    <div
      className="animate-spin rounded-full border-2 border-text-dim border-t-accent"
      style={{ width: size, height: size }}
    />
  );
}
